/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step1-name.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { Dispatch, forwardRef, SetStateAction, useEffect, useImperativeHandle, useState } from 'react';
import { FormControl, TextField } from '@mui/material';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useTranslation } from 'hooks';
import { ReportDefinitionFields } from 'queries/generated/fragments.generated';
import { checkRequiredFields, validateFields } from 'utils/validation';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { AddEditCustomReportErrors, AddEditCustomReportState, CustomReportPage } from './index';

interface CustomReportStep1NameProps {
    show: boolean;
    reportDefinition: ReportDefinitionFields;
    state: AddEditCustomReportState;
    setState: Dispatch<SetStateAction<AddEditCustomReportState>>;
    updateErrorState: (errorState: AddEditCustomReportErrors, pageNumber: number) => void;
    isNameUnique: () => boolean;
    canViewInternalRisks: boolean;
    updateRiskInternalColumns: (include: boolean) => void;
}

const CustomReportStep1Name = forwardRef(
    (
        {
            show,
            reportDefinition,
            state,
            setState,
            updateErrorState,
            isNameUnique,
            canViewInternalRisks,
            updateRiskInternalColumns,
        }: CustomReportStep1NameProps,
        ref,
    ) => {
        const { t } = useTranslation();

        const initialErrors: AddEditCustomReportErrors = {
            name: '',
            description: '',
        };

        const [errors, setErrors] = useState({ ...initialErrors } as AddEditCustomReportErrors);

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            const error = validateFields(value, field, length, required);
            setErrors({
                ...errors,
                ...error,
            });
        };

        const checkNameUnique = () => {
            if (isNameUnique()) return true;

            const uniqueNameError = t('reports:customReportNonUniqueNameError');
            const nameError = errors.name ? `${errors.name} ${uniqueNameError}` : uniqueNameError;

            setErrors({
                ...errors,
                name: nameError,
            });

            return false;
        };

        function validationOnUpdate() {
            const requiredFields = ['name'];

            if (!checkRequiredFields({ fields: requiredFields, state: state.reportDefinition, errors, setErrors }))
                return false;

            return checkNameUnique();
        }

        useImperativeHandle(ref, () => ({
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        const onChange = (fieldName: string, length?: number, required?: boolean) => (event: any) => {
            const { value } = event.target;
            setState({
                ...state,
                reportDefinition: {
                    ...reportDefinition,
                    [fieldName]: value,
                },
            });
            validate(value, fieldName, length, required);
        };

        const onInternalRisksCheckboxChange = (event: any) => {
            const { value } = event;

            setState({
                ...state,
                reportDefinition: {
                    ...reportDefinition,
                    includeInternalRisks: value,
                },
            });

            updateRiskInternalColumns(value);
        };

        const onNotApplicableRisksCheckboxChange = (event: any) => {
            const { value } = event;

            setState({
                ...state,
                reportDefinition: {
                    ...reportDefinition,
                    includeNotApplicableRisks: value,
                },
            });
        };

        const onDraftRisksCheckboxChange = (event: any) => {
            const { value } = event;

            setState({
                ...state,
                reportDefinition: {
                    ...reportDefinition,
                    includeDraftRisks: value,
                },
            });
        };

        const onObsoleteRisksCheckboxChange = (event: any) => {
            const { value } = event;

            setState({
                ...state,
                reportDefinition: {
                    ...reportDefinition,
                    includeObsoleteRisks: value,
                },
            });
        };

        useEffect(() => {
            updateErrorState(errors, CustomReportPage.step1Name);
        }, [errors]);

        return (
            <>
                {show && (
                    <div className="step1-name">
                        <div className="checkboxes-wrapper">
                            <div className="left-part k-w-full k-flex-wrap k-shrink-0">
                                {canViewInternalRisks && (
                                    <div className="!k-flex-basis-2/5">
                                        <Checkbox
                                            rounded={null}
                                            id="includeInternalRisksCheckbox"
                                            className="cell-checkbox no-border mr"
                                            checked={state.reportDefinition.includeInternalRisks}
                                            onChange={onInternalRisksCheckboxChange}
                                            label={t('reports:includeInternalRisks')}
                                            data-testid="include-internal-risks"
                                            size="medium"
                                        />
                                    </div>
                                )}
                                <div className="!k-flex-basis-2/5">
                                    <Checkbox
                                        rounded={null}
                                        id="includeDraftRisksCheckbox"
                                        className="cell-checkbox no-border mr"
                                        checked={state.reportDefinition.includeDraftRisks}
                                        onChange={onDraftRisksCheckboxChange}
                                        label={t('reports:includeDraftRisks')}
                                        data-testid="include-draft-risks"
                                        size="medium"
                                    />
                                </div>
                                <div className="!k-flex-basis-2/5">
                                    <Checkbox
                                        rounded={null}
                                        id="includeObsoleteRisksCheckbox"
                                        className="cell-checkbox no-border mr"
                                        checked={state.reportDefinition.includeObsoleteRisks}
                                        onChange={onObsoleteRisksCheckboxChange}
                                        label={t('reports:includeObsoleteRisks')}
                                        data-testid="include-obsolete-risks"
                                        size="medium"
                                    />
                                </div>
                                <div className="!k-flex-basis-2/5">
                                    <Checkbox
                                        rounded={null}
                                        id="includeNotApplicableRisksCheckbox"
                                        className="cell-checkbox no-border mr"
                                        checked={state.reportDefinition.includeNotApplicableRisks}
                                        onChange={onNotApplicableRisksCheckboxChange}
                                        label={t('reports:includeNotApplicableRisks')}
                                        data-testid="include-not-applicable-risks"
                                        size="medium"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="type-edc-wrapper mt">
                            <FormControl variant="standard" className="form-control-type full-width">
                                <TextField
                                    label={t('reports:customReportStep1Name')}
                                    id="add-edit-report-config-name"
                                    className="add-edit-report-config-name"
                                    fullWidth
                                    variant="outlined"
                                    value={reportDefinition.name}
                                    onChange={onChange('name', 100, true)}
                                    placeholder={t('reports:customReportStep1NameHint')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-edit-report-config-name"
                                            length={reportDefinition.name.length}
                                            max={100}
                                            error={errors.name}
                                        />
                                    }
                                    color="secondary"
                                    error={!!errors.name}
                                    data-testid="add-edit-report-config-name"
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper mt">
                            <FormControl variant="standard" className="form-control-type full-width">
                                <TextField
                                    label={t('reports:customReportStep1Description')}
                                    id="add-edit-report-config-description"
                                    className="add-edit-report-config-description"
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    rows={5}
                                    value={reportDefinition.description}
                                    onChange={onChange('description', 600, false)}
                                    placeholder={t('reports:customReportStep1DescriptionHint')}
                                    helperText={
                                        <CharactersRemaining
                                            id="add-edit-report-config-description"
                                            length={reportDefinition.description.length}
                                            max={600}
                                            error={errors.description}
                                        />
                                    }
                                    color="secondary"
                                    error={!!errors.description}
                                    data-testid="add-edit-report-config-description"
                                />
                            </FormControl>
                        </div>
                    </div>
                )}
            </>
        );
    },
);

export default CustomReportStep1Name;
